import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAffiliates } from "@/hooks/use-affiliates";
import { Eye, EyeOff, ShieldCheck, Plus, X, Users } from "lucide-react";
import { cn } from "@/lib/utils";

interface VisibilityData {
  visibility: string;
  affiliates: Array<{
    id: number;
    affiliateId: number;
    affiliateName: string;
    approvalStatus: string;
    status: string;
  }>;
}

const visibilityOptions = [
  { value: "public", label: "Public", description: "Visible to all affiliates", icon: Eye },
  { value: "require_approval", label: "Require Approval", description: "Affiliates must apply to access", icon: ShieldCheck },
  { value: "private", label: "Private", description: "Only visible to allowed affiliates", icon: EyeOff },
];

export function OfferVisibilityTab({ offerId }: { offerId: string }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["offers", offerId, "visibility"],
    queryFn: async () => {
      const res = await api.get(`/v1/offers/${offerId}/visibility`);
      if (!res.ok) throw new Error("Failed to fetch visibility");
      return res.json();
    },
  });

  const visData: VisibilityData | undefined = data?.data;
  const [selectedVisibility, setSelectedVisibility] = useState<string>("");
  const [showAddAffiliate, setShowAddAffiliate] = useState(false);

  useEffect(() => {
    if (visData) setSelectedVisibility(visData.visibility);
  }, [visData]);

  const updateMutation = useMutation({
    mutationFn: async (payload: { visibility: string; affiliateIds?: number[] }) => {
      const res = await api.put(`/v1/offers/${offerId}/visibility`, payload);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to update visibility");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "visibility"] });
      queryClient.invalidateQueries({ queryKey: ["offers", offerId] });
    },
  });

  function handleVisibilityChange(v: string) {
    setSelectedVisibility(v);
    updateMutation.mutate({ visibility: v });
  }

  if (isLoading) {
    return <div className="h-48 bg-gray-100 rounded-xl animate-pulse" />;
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-700">Visibility Type</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {visibilityOptions.map((opt) => {
            const Icon = opt.icon;
            const active = selectedVisibility === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => handleVisibilityChange(opt.value)}
                disabled={updateMutation.isPending}
                className={cn(
                  "flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-colors",
                  active ? "border-gray-900 bg-gray-50" : "border-gray-200 hover:border-gray-300",
                )}
              >
                <Icon className={cn("w-5 h-5 shrink-0 mt-0.5", active ? "text-gray-900" : "text-gray-400")} />
                <div>
                  <p className={cn("text-sm font-medium", active ? "text-gray-900" : "text-gray-700")}>{opt.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{opt.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {selectedVisibility === "private" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700">
              Allowed Affiliates ({visData?.affiliates?.length ?? 0})
            </h3>
            <button
              onClick={() => setShowAddAffiliate(true)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 hover:underline"
            >
              <Plus className="w-4 h-4" />
              Add Affiliate
            </button>
          </div>

          {visData?.affiliates && visData.affiliates.length > 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
              {visData.affiliates.map((a) => (
                <div key={a.id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{a.affiliateName}</span>
                    <span className="text-xs text-gray-400">#{a.affiliateId}</span>
                  </div>
                  <span className="text-xs capitalize text-gray-500">{a.approvalStatus}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 bg-white rounded-xl border border-gray-200">
              <EyeOff className="w-6 h-6 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No affiliates have access. Add affiliates to the allowlist.</p>
            </div>
          )}
        </div>
      )}

      {selectedVisibility === "require_approval" && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm text-amber-800">
            Affiliates can see this offer but must submit an application to access it.
            Manage applications from the <strong>Applications</strong> page in the sidebar.
          </p>
        </div>
      )}

      {showAddAffiliate && (
        <AddAffiliateModal
          offerId={offerId}
          existingIds={(visData?.affiliates ?? []).map((a) => a.affiliateId)}
          onClose={() => setShowAddAffiliate(false)}
          onAdded={(ids) => {
            updateMutation.mutate({
              visibility: "private",
              affiliateIds: [...(visData?.affiliates?.map((a) => a.affiliateId) ?? []), ...ids],
            });
            setShowAddAffiliate(false);
          }}
        />
      )}
    </div>
  );
}

function AddAffiliateModal({
  offerId,
  existingIds,
  onClose,
  onAdded,
}: {
  offerId: string;
  existingIds: number[];
  onClose: () => void;
  onAdded: (ids: number[]) => void;
}) {
  const { data: affData } = useAffiliates({ pageSize: 200 });
  const allAffiliates = affData?.data ?? [];
  const available = allAffiliates.filter((a: any) => !existingIds.includes(a.id));
  const [selected, setSelected] = useState<number[]>([]);

  function toggle(id: number) {
    setSelected((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Add Affiliates</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        {available.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">All affiliates are already added.</p>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-1 mb-4">
            {available.map((a: any) => (
              <label key={a.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.includes(a.id)}
                  onChange={() => toggle(a.id)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-900">{a.name}</span>
                <span className="text-xs text-gray-400">#{a.id}</span>
              </label>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={() => onAdded(selected)}
            disabled={selected.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50"
          >
            Add {selected.length > 0 ? `(${selected.length})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
